<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogDescription,
  } from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';

  import TitleEditor from './TitleEditor.svelte';
  import DateEditor from './DateEditor.svelte';
  import DescriptionEditor from './DescriptionEditor.svelte';
  import PictureEditor from './PictureEditor.svelte';
  import Picture from '../Picture.svelte';
  import StampPicker from './StampPicker.svelte';

  import { BlockEditorModel } from './models/BlockEditorModel.svelte';
  import SizeEditor from "./SizeEditor.svelte";
  import PrintRunEditor from "./PrintRunEditor.svelte";
  import CatalogIdsEditor from "./CatalogIdsEditor.svelte";
  import ZhihaoEditor from "./ZhihaoEditor.svelte";
  import LabelsEditor from "./LabelsEditor.svelte";
  import { getLabelsForType } from "$lib/philatelic-labels";
  import { PhilatelicItemType } from "@briefmarken_katalog/types";
  import { t } from '$lib/i18n';
  import { createBindableState, type BindableValue } from './utils.svelte';
  import HelpButton from "../HelpButton.svelte";

  interface Props {
    isOpen: BindableValue<boolean>;
    model: BlockEditorModel;
  }

  let { isOpen = $bindable(false), model = $bindable() }: Props = $props();

  let open = createBindableState(
    () => isOpen,
    (v) => isOpen = v,
    false
  );
</script>

<Dialog bind:open={open.current}>
  <DialogContent class="sm:max-w-2xl flex flex-col overflow-hidden max-h-[90dvh]">
    <div class="flex items-center gap-2">
      <DialogTitle>{$t(model.dialogTitle)}</DialogTitle>
      <HelpButton sectionId="block-editor" />
    </div>
    <DialogDescription>{$t(model.dialogSubtitle)}{model.dialogSubtitleSuffix}</DialogDescription>
    <div class="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
      <TitleEditor isReadonly={model.isReadonly} bind:title={model.title} invalid={!model.isReadonly && model.isTitleInvalid}/>
      <DescriptionEditor
        isReadonly={model.isReadonly}
        bind:description={model.description}
      />
      <DateEditor
        isReadonly={model.isReadonly}
        bind:date={model.date_of_issue}
        bind:expiryDate={model.expiry_date}
        invalid={!model.isReadonly && model.isDateInvalid}
      />
{#if model.pictures.length > 0}
      <Picture picture={model.pictures[0]}/>
{/if}
{#if !model.isReadonly}
      <PictureEditor
        isReadonly={model.isReadonly}
        hasPicture={model.pictures.length > 0}
        handleImageUpload={model.handleImageUpload.bind(model)}
        onPhoneUpload={model.setPictureFromPhone.bind(model)}
      />
{/if}
      <StampPicker
        isReadonly={model.isReadonly}
        bind:stampIds={model.stampIds}
        invalid={!model.isReadonly && model.isStampIdsInvalid}
      />
      <SizeEditor isReadonly={model.isReadonly} bind:width={model.size_width} bind:height={model.size_height} />
      <PrintRunEditor isReadonly={model.isReadonly} bind:printRun={model.print_run} />
      <div class="flex gap-2">
        <ZhihaoEditor isReadonly={model.isReadonly} bind:zhihao={model.zhihao} dateOfIssue={model.date_of_issue} containerClass="contents" />
        <CatalogIdsEditor isReadonly={model.isReadonly} bind:catalogIds={model.catalog_ids} containerClass="contents" />
        <LabelsEditor isReadonly={model.isReadonly} bind:labels={model.categories} availableLabels={getLabelsForType(PhilatelicItemType.Block)} containerClass="contents" />
      </div>
    </div>

    {#if model.showError}
      <div class="p-4 text-sm text-red-500 bg-red-50 rounded-md">
        {model.error ? $t(model.error) : ''}
      </div>
    {/if}

    {#if model.isReview}
      <DialogFooter>
        {#if model.isDirty}
          <form onsubmit={(ev: Event) => { model.onsubmit(() => { open.current = false; }, ev) }}>
            <Button type="submit" disabled={!model.isValid}>{$t('editor.save')}</Button>
          </form>
        {/if}
        <Button onclick={() => model.onreview('Accepted', () => { open.current = false; })}>{$t('editor.accept')}</Button>
        <Button onclick={() => model.onreview('Rejected', () => { open.current = false; })}>{$t('editor.reject')}</Button>
      </DialogFooter>
    {:else if model.isDelete}
      <div class="p-4 text-lg font-bold text-red-600 bg-red-50 rounded-md text-center">
        {$t('deleteSuggestion.warning')}
      </div>
      <DialogFooter>
        <Button variant="outline" onclick={() => open.current = false}>{$t('deleteSuggestion.cancel')}</Button>
        <Button variant="destructive" onclick={() => model.ondelete(() => { open.current = false; })}>{$t('deleteSuggestion.confirm')}</Button>
      </DialogFooter>
    {:else if model.showSubmitButton}
      <form onsubmit={(ev: Event) => { model.onsubmit(() => { open.current = false; }, ev) }}>
        <DialogFooter>
          <Button type="submit" disabled={!model.isDirty || !model.isValid}>{$t(model.isEdit ? 'editor.save' : 'editor.addBlockSuggestion')}</Button>
        </DialogFooter>
      </form>
    {/if}
  </DialogContent>
</Dialog>
